import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import OnboardingTour from '@/components/OnboardingTour';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Plus, 
  Home, 
  Eye, 
  Edit, 
  Clock, 
  AlertCircle,
  ArrowRight,
  Archive,
  Trash2,
  BarChart3,
  TrendingUp,
  Users,
  UserCircle,
  CheckCircle2,
  Crown,
  Sparkles,
  Calculator
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  Draft: 'bg-gray-100 text-gray-700',
  Active: 'bg-green-100 text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Sold: 'bg-blue-100 text-blue-700',
  Archived: 'bg-slate-100 text-slate-600'
};

const Dashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [usage, setUsage] = useState({ active_listings: 0, listing_limit: 3 });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('active');
  const location = useLocation();

  const activeListings = listings.filter(l => ['Active', 'Pending', 'Draft'].includes(l.status));
  const archivedListings = listings.filter(l => ['Sold', 'Archived'].includes(l.status));

  useEffect(() => {
    fetchData();
    checkProfileCompletion();
  }, [location.key]);

  const checkProfileCompletion = async () => {
    try {
      const response = await axios.get(`${API}/profile/completion-status`, { headers: getAuthHeaders() });
      setProfileStatus(response.data);
      if (response.data.should_redirect) {
        setShowProfileModal(true);
      }
    } catch (error) {
      console.error('Profile check error:', error);
    }
  };

  const handleGoToProfile = () => {
    setShowProfileModal(false);
    navigate('/account');
  };

  const handleSkipProfile = async () => {
    try {
      await axios.post(`${API}/profile/mark-completed`, {}, { headers: getAuthHeaders() });
      setShowProfileModal(false);
    } catch (error) {
      setShowProfileModal(false);
    }
  };

  const fetchData = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/dashboard`, { 
        headers: getAuthHeaders(),
        timeout: 30000 // 30 second timeout
      });
      setListings(response.data.listings);
      setUsage(response.data.usage);
      
      const hideOnboarding = localStorage.getItem('hideOnboardingTour') === 'true';
      if (!response.data.onboarding.completed && !hideOnboarding) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      // Retry up to 3 times with increasing delay
      if (retryCount < 3) {
        console.log(`Retrying dashboard fetch (attempt ${retryCount + 1})...`);
        const delay = (retryCount + 1) * 1500; // 1.5s, 3s, 4.5s
        setTimeout(() => fetchData(retryCount + 1), delay);
        return;
      }
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      if (retryCount === 0 || retryCount >= 3) {
        setLoading(false);
      }
    }
  };

  const handleCreateListing = () => {
    // Allow if listing_limit is "unlimited" (999999 or greater) or hasn't reached limit
    if (usage.listing_limit < 999 && usage.active_listings >= usage.listing_limit) {
      toast.error(`You've reached your limit of ${usage.listing_limit} active listings. Archive a listing or upgrade your plan.`);
      return;
    }
    navigate('/listings/new');
  };

  const handleArchiveListing = async (listingId, currentStatus) => {
    const newStatus = currentStatus === 'Archived' ? 'Draft' : 'Archived';
    const action = currentStatus === 'Archived' ? 'restored' : 'archived';
    
    try {
      await axios.put(
        `${API}/listings/${listingId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      setListings(prev => prev.map(l => 
        l.id === listingId ? { ...l, status: newStatus } : l
      ));
      
      toast.success(`Listing ${action} successfully`);
    } catch (error) {
      console.error('Archive error:', error);
      toast.error(`Failed to ${action.slice(0, -1)} listing`);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!confirm('Are you sure you want to permanently delete this listing? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/listings/${listingId}`,
        { headers: getAuthHeaders() }
      );
      
      setListings(prev => prev.filter(l => l.id !== listingId));
      toast.success('Listing deleted permanently');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete listing');
    }
  };

  const trialDaysRemaining = () => {
    if (user?.plan !== 'Trial') return null;
    const trialEnd = new Date(user.trial_end_date);
    const now = new Date();
    const days = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const usagePercentage = (usage.active_listings / usage.listing_limit) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <Navbar variant="app" />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="dashboard">
      <Navbar variant="app" />
      
      {showOnboarding && (
        <OnboardingTour onClose={() => setShowOnboarding(false)} />
      )}
      
      {/* Profile Completion Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="h-6 w-6 text-primary" />
              Complete Your Profile
            </DialogTitle>
            <DialogDescription className="text-left">
              Your profile information will appear on all your shared listings. Complete your profile to make a great first impression!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Profile completion</span>
                <span className="font-medium">{profileStatus?.completion_percentage || 0}%</span>
              </div>
              <Progress value={profileStatus?.completion_percentage || 0} className="h-2" />
            </div>
            
            {profileStatus?.missing_required?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-red-600">Required:</p>
                <ul className="space-y-1">
                  {profileStatus.missing_required.map((item) => (
                    <li key={item.field} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {profileStatus?.missing_recommended?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-amber-600">Recommended:</p>
                <ul className="space-y-1">
                  {profileStatus.missing_recommended.map((item) => (
                    <li key={item.field} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                      {item.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="ghost" onClick={handleSkipProfile}>
              Skip for now
            </Button>
            <Button onClick={handleGoToProfile} className="gap-2">
              <UserCircle className="h-4 w-4" />
              Complete Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Stats in Top Right */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-muted-foreground mt-1">Here's what's happening with your listings</p>
          </div>
          
          {/* Compact Stats - Top Right */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Active Listings Badge */}
            <div className="flex items-center gap-2 bg-card border rounded-full px-4 py-2">
              <Home size={16} className="text-primary" />
              <span className="text-sm font-medium">{usage.active_listings}/{usage.listing_limit >= 999 || usage.listing_limit < 0 ? 'Unlimited' : usage.listing_limit}</span>
              <span className="text-xs text-muted-foreground">listings</span>
            </div>
            
            {/* Plan Badge */}
            <Link to="/account">
              <div className="flex items-center gap-2 bg-card border rounded-full px-4 py-2 hover:border-primary/50 transition-colors cursor-pointer">
                <Crown size={16} className={user?.plan === 'Free' ? 'text-yellow-500' : 'text-primary'} />
                <span className="text-sm font-medium">{user?.plan}</span>
                {user?.plan === 'Free' && (
                  <Badge variant="secondary" className="text-xs">Limited</Badge>
                )}
              </div>
            </Link>
          </div>
        </div>

        {/* Upgrade Banner - Only show for Free users */}
        {user?.plan === 'Free' && (
          <Card className="mb-6 border-blue-500/30 bg-blue-500/5">
            <CardContent className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-4">
              <div className="flex items-center gap-3">
                <Crown className="text-blue-600" size={24} />
                <div>
                  <p className="font-semibold text-foreground">
                    Unlock unlimited listings for just $20
                  </p>
                  <p className="text-sm text-muted-foreground">
                    One-time payment, lifetime access to all features
                  </p>
                </div>
              </div>
              <Link to="/account">
                <Button className="rounded-full" data-testid="upgrade-btn">
                  Upgrade Now
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Main 4-Section Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Listings Card */}
          <Card 
            className="border-2 border-dashed border-primary/30 hover:border-primary/60 bg-primary/5 transition-all cursor-pointer group"
            onClick={handleCreateListing}
            data-testid="create-listing-card"
          >
            <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px] text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Plus size={28} className="text-primary" />
              </div>
              <h3 className="font-semibold text-lg mb-2">New Listing</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload photos & generate AI content
              </p>
              <Button size="sm" className="rounded-full gap-2 group-hover:bg-primary group-hover:text-white transition-colors">
                <Sparkles size={14} />
                Get Started
              </Button>
            </CardContent>
          </Card>

          {/* Market Data Card */}
          <Card 
            className="border-border/40 hover:border-blue-500/50 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => navigate('/market-data')}
          >
            <CardContent className="p-6 flex flex-col min-h-[200px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white group-hover:scale-105 transition-transform">
                  <BarChart3 size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Market Data</h3>
                  <p className="text-xs text-muted-foreground">& Updates</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground flex-1">
                Real-time market trends & branded updates
              </p>
              <div className="flex items-center text-blue-600 text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                View Data
                <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* Contacts Card */}
          <Card 
            className="border-border/40 hover:border-green-500/50 hover:shadow-lg transition-all cursor-pointer group"
            onClick={() => navigate('/contacts')}
          >
            <CardContent className="p-6 flex flex-col min-h-[200px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 text-white group-hover:scale-105 transition-transform">
                  <Users size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Contacts</h3>
                  <p className="text-xs text-muted-foreground">CRM</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground flex-1">
                Manage clients, track follow-ups & pipeline
              </p>
              <div className="flex items-center text-green-600 text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                View Contacts
                <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </CardContent>
          </Card>

          {/* Mortgage Calculator Card */}
          <Card 
            className="border-border/40 hover:border-amber-500/50 hover:shadow-lg transition-all group"
            data-testid="calculator-card"
          >
            <CardContent className="p-6 flex flex-col min-h-[200px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                  <Calculator size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Calculators</h3>
                  <p className="text-xs text-muted-foreground">Client Tools</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start gap-2 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300"
                  onClick={() => navigate('/mortgage-calculator')}
                  data-testid="mortgage-calculator-btn"
                >
                  <Calculator size={14} />
                  Mortgage Calculator
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="justify-start gap-2 hover:bg-green-50 hover:text-green-700 hover:border-green-300"
                  onClick={() => navigate('/affordability-calculator')}
                  data-testid="affordability-calculator-btn"
                >
                  <Home size={14} />
                  Home Affordability
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Your Listings Section */}
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Your Listings</h2>
              <TabsList className="bg-muted/50">
                <TabsTrigger value="active" className="gap-2 data-[state=active]:bg-background">
                  <Home size={16} />
                  Active ({activeListings.length})
                </TabsTrigger>
                <TabsTrigger value="archive" className="gap-2 data-[state=active]:bg-background">
                  <Archive size={16} />
                  Archive ({archivedListings.length})
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Active Listings Tab */}
            <TabsContent value="active">
              {activeListings.length === 0 ? (
                <Card className="border-border/40 bg-muted/20">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
                      <Home className="text-muted-foreground" size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">No active listings yet</h3>
                    <p className="text-muted-foreground text-center mb-4 max-w-sm text-sm">
                      Create your first listing to start generating AI-powered marketing content
                    </p>
                    <Button 
                      onClick={handleCreateListing}
                      className="rounded-full gap-2"
                      data-testid="empty-create-btn"
                    >
                      <Plus size={18} />
                      Create Your First Listing
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeListings.map((listing) => {
                    const photo = listing.photos?.[0];
                    const photoUrl = photo?.thumbnail_url || photo?.staged_photo_url || photo?.enhanced_photo_url || photo?.original_photo_url;
                    
                    return (
                    <Card 
                      key={listing.id} 
                      className="border-border/40 hover:shadow-md transition-shadow overflow-hidden group"
                    >
                      {/* Listing Image */}
                      <div className="relative h-40 bg-muted overflow-hidden">
                        {photoUrl ? (
                          <img 
                            src={photoUrl}
                            alt={listing.address}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                            <Home className="text-muted-foreground" size={32} />
                          </div>
                        )}
                        <Badge className={`absolute top-3 left-3 ${statusColors[listing.status]}`}>
                          {listing.status}
                        </Badge>
                      </div>
                      
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground truncate mb-1">
                          {listing.address || 'Untitled Listing'}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {listing.bedrooms || 0} beds · {listing.bathrooms || 0} baths · {listing.sqft?.toLocaleString() || 0} sqft
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="flex-1"
                            onClick={() => navigate(`/listings/${listing.id}`)}
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveListing(listing.id, listing.status);
                            }}
                          >
                            <Archive size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </TabsContent>

            {/* Archive Tab */}
            <TabsContent value="archive">
              {archivedListings.length === 0 ? (
                <Card className="border-border/40 bg-muted/20">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Archive className="text-muted-foreground mb-4" size={32} />
                    <h3 className="font-semibold text-foreground mb-2">No archived listings</h3>
                    <p className="text-muted-foreground text-sm">Sold or archived listings will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {archivedListings.map((listing) => {
                    const photo = listing.photos?.[0];
                    const photoUrl = photo?.thumbnail_url || photo?.staged_photo_url || photo?.enhanced_photo_url || photo?.original_photo_url;
                    
                    return (
                    <Card 
                      key={listing.id} 
                      className="border-border/40 opacity-75 hover:opacity-100 transition-opacity overflow-hidden"
                    >
                      <div className="relative h-32 bg-muted overflow-hidden">
                        {photoUrl ? (
                          <img 
                            src={photoUrl}
                            alt={listing.address}
                            className="w-full h-full object-cover grayscale"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                            <Home className="text-muted-foreground" size={24} />
                          </div>
                        )}
                        <Badge className={`absolute top-2 left-2 ${statusColors[listing.status]}`}>
                          {listing.status}
                        </Badge>
                      </div>
                      
                      <CardContent className="p-3">
                        <h3 className="font-medium text-foreground truncate text-sm mb-2">
                          {listing.address || 'Untitled Listing'}
                        </h3>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="flex-1 text-xs"
                            onClick={() => handleArchiveListing(listing.id, listing.status)}
                          >
                            Restore
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteListing(listing.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )})}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
